import { apiClient, getApiUrl } from '../lib/apiClient';
import type {
  Area,
  ListQueryParams,
  Pagination,
  CreateAreaRequest,
  UpdateAreaRequest
} from '../types/api';
import {
  extractListData,
  extractSingleData,
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse
} from '../utils/railsResponseHandler';

// Area CRUD operations
export const getAreas = async (params?: ListQueryParams): Promise<{ areas: Area[]; pagination: Pagination }> => {
  const response = await apiClient.get<RailsWrappedListResponse<Area>>(getApiUrl('/areas'), { params });

  const areas = extractListData<Area>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: areas.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };

  return { areas, pagination };
};

export const getArea = async (id: string, view?: string): Promise<Area> => {
  const params = view ? { view } : {};
  const response = await apiClient.get<RailsSingleWrappedResponse<Area>>(getApiUrl(`/areas/${id}`), { params });
  return extractSingleData<Area>(response.data);
};

export const createArea = async (data: CreateAreaRequest): Promise<Area> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<Area>>(getApiUrl('/areas'), data);
  return extractSingleData<Area>(response.data);
};

export const updateArea = async (id: string, data: UpdateAreaRequest): Promise<Area> => {
  const response = await apiClient.patch<RailsSingleWrappedResponse<Area>>(getApiUrl(`/areas/${id}`), data);
  return extractSingleData<Area>(response.data);
};

export const deleteArea = async (id: string): Promise<void> => {
  await apiClient.delete(getApiUrl(`/areas/${id}`));
};

// School-specific area operations
export const getSchoolAreas = async (schoolId: string, params?: ListQueryParams): Promise<{ areas: Area[]; pagination: Pagination }> => {
  const response = await apiClient.get<RailsWrappedListResponse<Area>>(getApiUrl(`/schools/${schoolId}/areas`), { params });

  const areas = extractListData<Area>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: areas.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };

  return { areas, pagination };
};

// Area-camera assignment operations
export const getAreaCameras = async (areaId: string): Promise<any[]> => {
  try {
    const response = await apiClient.get<RailsWrappedListResponse<any>>(getApiUrl(`/areas/${areaId}/cameras`));
    return extractListData(response.data);
  } catch (error) {
    console.error('Error getting area cameras:', error);
    return [];
  }
};

export const assignCameraToArea = async (areaId: string, cameraId: string): Promise<void> => {
  await apiClient.post(getApiUrl(`/areas/${areaId}/cameras`), { camera_id: cameraId });
};

export const removeCameraFromArea = async (areaId: string, cameraId: string): Promise<void> => {
  await apiClient.delete(getApiUrl(`/areas/${areaId}/cameras/${cameraId}`));
};

// Area statistics and overview
export const getAreaOverview = async (): Promise<{
  total: number;
  by_type: Record<string, number>;
  active: number;
  inactive: number;
  requires_supervision: number;
}> => {
  try {
    const { areas } = await getAreas({ per_page: 100 });

    const total = areas.length;
    let active = 0;
    let inactive = 0;
    let requires_supervision = 0;
    const by_type: Record<string, number> = {};

    areas.forEach(area => {
      // Count active/inactive
      if (area.active) {
        active++;
      } else {
        inactive++;
      }

      // Count supervision requirements
      if ((area as any).requires_supervision) {
        requires_supervision++;
      }

      // Count by type
      const areaType = area.area_type || 'unknown';
      by_type[areaType] = (by_type[areaType] || 0) + 1;
    });

    return {
      total,
      by_type,
      active,
      inactive,
      requires_supervision,
    };
  } catch (error) {
    console.error('Error getting area overview:', error);
    throw error;
  }
};
